import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import { formatDurationWithSessions } from "../../../utils/utils"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { getElementTextColor, generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme, getButtonClasses, getThemeShapeClasses } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl, buildBookingUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const PackagesSectionDesktop = ({ section, practice, theme, highlightedField, pageSlug, defaultPageType = null }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || "Browse Packages"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || "packages"
  const selectedPackageIds = settings.selectedPackageIds || []

  // Get theme colors with fallback generation for bgMedium and bgExtraLight
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium or bgExtraLight are missing, generate them
    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Always use bgLight from theme for section background
  const sectionBackgroundColor = themeColors.bgLight || "#FFFFFF"
  const headingColor = getElementTextColor("heading", sectionBackgroundColor, theme)
  const bodyColor = getElementTextColor("body", sectionBackgroundColor, theme)

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const handlePackageClick = (packageId) => {
    window.location.href = buildServiceUrl(packageId, practice)
  }

  // Get practice packages
  const allPracticePackages = practice?.packages || []

  // If specific packages are selected, use those; otherwise use all packages
  const selectedPackages =
    selectedPackageIds.length > 0
      ? allPracticePackages.filter((pkg) => selectedPackageIds.includes(pkg.id))
      : allPracticePackages

  // Combine packages, limit based on layout and page (by defaultPageType or slug fallback)
  const isHomePage = defaultPageType === "home" || (defaultPageType === null && pageSlug === "home")
  const maxItems = isHomePage
    ? layout === "highlights_2"
      ? 2
      : layout === "highlights_3"
      ? 3
      : 3 // Limit to 3 for list layout when on home page
    : 999 // Show all items on non-home pages
  const allItems = selectedPackages.slice(0, maxItems)
  const hasMorePackages = selectedPackages.length > maxItems

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    return `$${amount.toFixed(2)}${currency === "CAD" ? " CAD" : ""}`
  }

  // Render package card for highlights layout (image-based design)
  const renderHighlightCard = (item, index) => {
    const price = item.amountCents ? formatPrice(item.amountCents, practice?.defaultCurrency || "USD") : ""
    const duration = item.timeLength ? formatDurationWithSessions(item.timeLength, item) : ""

    // Format location and service type information for highlights
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}` : null)

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <div key={index} className="flex w-full flex-col items-center justify-start gap-[21px]">
        {/* Image (with placeholder fallback to preserve layout) */}
        <div className="aspect-[360/227] w-full overflow-hidden bg-gray-100">
          <img
            src={imageUrl || placeholderImage}
            alt={item.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
        </div>

        {/* Content */}
        <div className="flex w-full flex-col items-start justify-start gap-2 text-center">
          {/* Title */}
          <h3
            className="line-clamp-2 w-full text-[28px] font-bold leading-[40px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: headingColor,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}>
            {item.name}
          </h3>

          {/* Price and Package Badge */}
          <div className="flex w-full items-center justify-center gap-3">
            <span
              className="text-[20px] font-semibold"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: bodyColor
              }}>
              {price}
            </span>
            <span
              className={twMerge(
                "px-3 py-1 text-xs font-semibold uppercase tracking-wider",
                getThemeShapeClasses(theme)
              )}
              style={{
                backgroundColor: theme?.colors?.primary || "#BD654F",
                color: "#FFFFFF",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
              }}>
              {item.numberOfSessions ? `${item.numberOfSessions} Session Package` : "Package"}
            </span>
          </div>

          {/* Duration and Location */}
          {(duration || locationInfo) && (
            <p
              className="text-[16px] text-gray-600"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
              }}>
              {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
            </p>
          )}

          {/* Description */}
          {displayDescription && (
            <div
              className="prose prose-lg w-full max-w-none overflow-hidden text-[18px] leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: bodyColor
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}></div>
          )}

          {/* Learn more button */}
          <button
            onClick={() => handlePackageClick(item.id)}
            className="w-full cursor-pointer text-[16px] font-semibold leading-[24px] underline transition-opacity hover:opacity-80"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: theme?.colors?.primary || "#BD654F"
            }}>
            Learn more
          </button>
        </div>
      </div>
    )
  }

  // Render package card for list layout (horizontal row design)
  const renderPackageCard = (item, index) => {
    const price = item.amountCents ? formatPrice(item.amountCents, practice?.defaultCurrency || "USD") : ""

    // Format duration
    const duration = item.timeLength ? formatDurationWithSessions(item.timeLength, item) : ""

    // Format location and service type information
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""

    // Combine all location/service info with bullet points
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}` : null)

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <div key={index} className="relative flex items-center gap-[72px] border border-[#e6dad7] bg-white p-10">
        <div className="flex flex-1 items-center gap-8">
          <div className="shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.name}
                className="h-48 w-48 rounded bg-center bg-no-repeat object-cover"
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
            ) : (
              <div className="flex h-48 w-48 items-center justify-center rounded bg-gray-200">
                <span className="text-gray-400">No image</span>
              </div>
            )}
          </div>

          <div className="flex-1 space-y-2">
            <div>
              {price && (
                <p
                  className="text-[20px] font-semibold leading-[32px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    color: bodyColor
                  }}>
                  {price}
                </p>
              )}
              <h3
                className="text-[28px] font-bold leading-[40px]"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  color: headingColor,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}>
                {item.name}
              </h3>
            </div>

            {(duration || locationInfo) && (
              <p
                className="whitespace-pre text-[16px] font-normal leading-[24px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: bodyColor
                }}>
                {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
              </p>
            )}

            {displayDescription && (
              <div
                className="prose prose-lg max-w-none overflow-hidden text-[18px] font-normal leading-[32px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: bodyColor,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical"
                }}
                dangerouslySetInnerHTML={{ __html: displayDescription }}
              />
            )}
          </div>
        </div>

        <div className="shrink-0">
          <button
            onClick={() => handlePackageClick(item.id)}
            className="px-10 py-3 text-[16px] font-normal leading-[24px] text-white transition-all hover:shadow-lg"
            style={applyButtonTheme(
              {
                backgroundColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                borderRadius: theme?.borderRadius || "8px"
              },
              theme
            )}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
            }}>
            Details
          </button>
        </div>
      </div>
    )
  }

  const getGridClasses = () => {
    switch (layout) {
      case "highlights_2":
        return "grid-cols-1 md:grid-cols-2 gap-6" // 50% width columns for 2 highlights
      case "highlights_3":
        return "grid-cols-1 md:grid-cols-3"
      case "list":
      default:
        return "flex flex-col gap-6" // Vertical stack for list layout - matches Figma gap-6
    }
  }

  const isHighlightLayout = layout === "highlights_2" || layout === "highlights_3"

  return (
    <section
      className={twMerge("relative scroll-mt-24 py-14 pb-[72px]", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <AnimatedSection className="mx-auto max-w-[1160px] px-0">
        <div className="flex w-full flex-col items-start gap-16">
          {/* Header */}
          <div className="flex w-full flex-col items-start gap-8">
            {/* Title and Browse Services Button */}
            <div className="flex w-full items-center justify-between gap-10">
              <div className="flex flex-col items-start gap-4">
                <h2
                  className="text-left text-[40px] font-bold leading-[48px]"
                  style={{
                    fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                    fontWeight: theme?.fonts?.headingWeight || "700",
                    color: headingColor
                  }}>
                  {section.headline || "Packages"}
                </h2>
                {section.body && (
                  <p
                    className="max-w-3xl text-left text-xl leading-relaxed"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                      color: bodyColor
                    }}>
                    {section.body}
                  </p>
                )}
              </div>

              {/* Browse Services Button - Not shown on packages section (this is the packages page) */}
            </div>
          </div>

          {/* Packages Grid */}
          {allItems.length > 0 && (
            <div className="w-full">
              {isHighlightLayout ? (
                // Highlights layout - grid layout
                <div className={`grid ${getGridClasses()}`}>
                  {allItems.map((item, index) => renderHighlightCard(item, index))}
                </div>
              ) : (
                // List layout - vertical stack
                <div className={getGridClasses()}>{allItems.map((item, index) => renderPackageCard(item, index))}</div>
              )}
            </div>
          )}

          {/* Empty State */}
          {allItems.length === 0 && (
            <div className="flex flex-col items-center gap-6 py-16 text-center">
              <div className="text-6xl">📦</div>
              <h3
                className="text-2xl font-semibold"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  color: headingColor
                }}>
                No packages available
              </h3>
              <p
                className="text-lg"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: bodyColor
                }}>
                {practice?.packages?.length === 0
                  ? "This practice hasn't added any packages yet."
                  : "No packages match the current selection."}
              </p>
            </div>
          )}

          {/* Main CTA Button - Only show if not on packages page */}
          {showCTA && allItems.length > 0 && pageSlug !== "packages" && (
            <div className="flex justify-center pt-8">
              {ctaAction === "booking" ? (
                <button
                  onClick={() => (window.location.href = buildPublicPageUrl("packages", practice))}
                  className={twMerge(
                    "inline-flex items-center justify-center px-10 py-3 text-base font-semibold text-white transition-all duration-300 hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      borderRadius: theme?.borderRadius || "12px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    e.target.style.transform = "translateY(-2px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    e.target.style.transform = "translateY(0)"
                  }}>
                  {ctaText}
                </button>
              ) : (
                <button
                  onClick={handleCTAClick}
                  className={twMerge(
                    "inline-flex items-center justify-center px-10 py-3 text-base font-semibold text-white transition-all duration-300 hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      borderRadius: theme?.borderRadius || "12px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    e.target.style.transform = "translateY(-2px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    e.target.style.transform = "translateY(0)"
                  }}>
                  {ctaText}
                </button>
              )}
            </div>
          )}

          {/* Show More Button for non-home pages */}
          {hasMorePackages && !isHomePage && (
            <div className="mt-8 text-center">
              <button
                onClick={() => (window.location.href = buildPublicPageUrl("services", practice))}
                className={twMerge("px-8 py-3 font-medium text-white transition-colors", getThemeShapeClasses(theme))}
                style={applyButtonTheme(
                  {
                    backgroundColor: theme?.colors?.primary || "#BD654F",
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    borderRadius: theme?.borderRadius || "12px"
                  },
                  theme
                )}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                  e.target.style.transform = "translateY(-2px)"
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                  e.target.style.transform = "translateY(0)"
                }}>
                View All Packages
              </button>
            </div>
          )}
        </div>
      </AnimatedSection>
    </section>
  )
}

export default PackagesSectionDesktop
